import { API } from "@swivl-mono/api-helper";
import { LocationScope, LocationVarKey, SpotData, SpotDataButton } from "@swivl-mono/types";
import { SpotMainLayoutComponent } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import { useMergedLocationVariables } from "../../../Hooks/useLocationVariables";
import { usePropertiesGetLocationIds } from "../../../Hooks/usePropertiesGetLocationIds";
import BrandsModel from "../../../Models/Brands/Brands.Model";
import LocationVarsModel from "../../../Models/Variables/LocationVars.Model";
import { SpotButtonsView } from "../Spot/Buttons/Spot.Buttons.View";
import { SpotButtonControls } from "../Spot/Sidebar/Spot.Button.Controls";
import "../Spot/Spot.View.scss";
import { SpotV3SidebarControls } from "./SpotV3.Sidebar.Controls";

/**
 * Property Manager v3 → spot.is (ENG-3138; Chuck, 2026-08-30).
 *
 * The v2 spot.is page, duplicated into v3 so operators edit the spot slugs the bot reads
 * (`CortexSpotBrandSlug`, `CortexSpotLocationSlug`) instead of the legacy keys. The preview is
 * fetched by those same slugs, so what is typed here is what it reflects. The buttons are spot's
 * own data (`SpotButtons`) and are shared with v2 unchanged. The v2 page is left alone.
 */
export function SpotV3View() {
  const page = usePropertiesGetLocationIds();
  const isLocationPage = page.type === LocationScope.LOCATION;
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [data, setData] = useState<SpotData | null>(null);
  const [selectedButtonId, setSelectedButtonId] = useState<string | undefined>(undefined);

  const brands = BrandsModel.useState((state) => state.brands);

  useEffect(() => {
    BrandsModel.load(false);
  }, []);

  const vars = useMergedLocationVariables(
    [LocationVarKey.CortexSpotBrandSlug, LocationVarKey.CortexSpotLocationSlug, LocationVarKey.SpotButtons],
    page.groupId,
    page.locationId
  );

  const buttons = useMemo(() => {
    if (vars && vars[LocationVarKey.SpotButtons]) {
      try {
        return JSON.parse(vars[LocationVarKey.SpotButtons].value) as SpotDataButton[];
      } catch (e) {
        console.error("Error parsing SpotData", e);
      }
    }
    return undefined;
  }, [vars]);

  const setButtons = async (next: SpotDataButton[]) => {
    if (vars && vars[LocationVarKey.SpotButtons] && vars[LocationVarKey.SpotButtons].id) {
      await LocationVarsModel.saveVariable(vars[LocationVarKey.SpotButtons].id, JSON.stringify(next));
    }
  };

  const { locationSlug, brandSlug } = useMemo(() => {
    if (vars) {
      return {
        locationSlug: vars[LocationVarKey.CortexSpotLocationSlug]?.value,
        brandSlug: vars[LocationVarKey.CortexSpotBrandSlug]?.value
      };
    }
    return { locationSlug: null, brandSlug: null };
  }, [vars]);

  useEffect(() => {
    if (!brandSlug) return;
    if (isLocationPage) {
      if (locationSlug) API.Spot.getLocation(brandSlug, locationSlug).then(setData);
    } else {
      API.Spot.getBrand(brandSlug).then(setData);
    }
  }, [locationSlug, brandSlug, isLocationPage]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDragging) setOffset({ x: e.clientX - position.x, y: e.clientY - position.y });
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(false);
    setPosition({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  return (
    <>
      <div
        key={"graphPaperBg" + page.path}
        className="graphPaperBg absolute inset-0 overflow-auto"
        onClick={() => setSelectedButtonId(undefined)}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ cursor: isDragging ? "grabbing" : "grab" }}
      />
      <div className="SpotView" style={{ transform: `translate(${offset.x}px, ${offset.y}px)` }}>
        {data && (
          <div className="absolute right-20 left-10 p-10 shadow-lg" style={{ width: "500px", height: "80vh", overflow: "hidden" }}>
            <SpotMainLayoutComponent key={"mainlayout" + page.path} position="absolute" data={data} rounded={true}>
              <SpotButtonsView
                page={page}
                buttons={buttons}
                setSelectedButtonId={setSelectedButtonId}
                selectedButtonId={selectedButtonId}
                buttonsVar={vars?.SpotButtons}
                setButtons={setButtons}
              />
            </SpotMainLayoutComponent>
          </div>
        )}
      </div>
      <div className="top-20vh fixed right-0 h-full w-96 bg-white shadow-[-4px_2px_8px_rgba(0,0,0,0.1)]">
        {selectedButtonId && buttons ? (
          <SpotButtonControls buttons={buttons} setButtons={setButtons} selectedButtonId={selectedButtonId} />
        ) : (
          <SpotV3SidebarControls page={page} brands={brands} />
        )}
      </div>
    </>
  );
}
